import _ from 'underscore';
import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { MessageBar, MessageBarType } from '@fluentui/react';
import PropTypes from 'prop-types';
import { Label, Stack, ChoiceGroup, Checkbox } from '@bingads-webui-react/fabric-control';
import { CampaignOData } from '@bingads-webui-react/campaign-odata';
import { withCustomValidation } from '@bingads-webui-react/validation';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { PlacementDataMapping } from '@bingads-webui-campaign/audience-campaign-shared';
import productivityIcon from '../../../media/productivity-placement.svg';
import homePagesIcon from '../../../media/home-pages-placement.svg';
import thematicBundleIcon from '../../../media/thematic-bundle-placement.svg';
import { placementsOptionStyle,
  placementsAllSpecificChoiceGroup,
  placementsCheckboxes,
  itemStyle,
  itemStyleChecked,
  itemStyleDisabled,
  placementCard,
  iconSection,
  iconSectionChecked,
  iconSectionDisabled,
  titleStyle,
  subheaderStyle } from './styles/placements-function-target-styles';


export const formatPlacements = ({ BoostAdunitIds, i18n }) => {
  if (!BoostAdunitIds || !_.isArray(BoostAdunitIds) || (_.isArray(BoostAdunitIds) && BoostAdunitIds.length === 0)) {
    return i18n.getString(_TL_('The entire Microsoft Advertising Network.'));
  }

  return i18n.getString(_TL_('Microsoft owned and operated placements.'));
};

const placements = {
  Productivity: {
    id: 1,
    label: _TL_('Productivity'),
    description: _TL_('Outlook and more.'),
    adUnitIds: PlacementDataMapping.Productivity.adUnitIds,
    icon: productivityIcon,
  },
  HomePages: {
    id: 2,
    label: _TL_('Home pages'),
    description: _TL_('New tab pages and more.'),
    adUnitIds: PlacementDataMapping.HomePages.adUnitIds,
    icon: homePagesIcon,
  },
  Games: {
    id: 3,
    label: _TL_('Games'),
    description: _TL_('Microsoft Casual Games and more.'),
    adUnitIds: PlacementDataMapping.Games.adUnitIds,
    icon: thematicBundleIcon,
  },
};

const placementsIdMap = {
  1: 'Productivity',
  2: 'HomePages',
  3: 'Games',
};

const options = [
  {
    key: 'all',
    text: _TL_('The entire Microsoft Advertising Network (recommended)'),
    onRenderField: (props, render) => (
      <div>
        {render(props)}
        <div className={placementsOptionStyle}>
          {_TL_('This option includes Microsoft sites and additional partner traffic.')}
        </div>
      </div>
    ),
    styles: { root: { fontWeight: '100' } },
  },
  {
    key: 'specific',
    text: _TL_('Microsoft owned and operated placements'),
    onRenderField: (props, render) => (
      <div>
        {render(props)}
        <div className={placementsOptionStyle}>
          {_TL_('Show your ads on specific Microsoft owned and operated placements.')}
        </div>
      </div>
    ),
    styles: { root: { fontWeight: '100' } },
  },
];

const renderPlacements = (props) => {
  const { checked, disabled } = props;
  let iconSectionStyle;
  if (checked) {
    iconSectionStyle = disabled ? iconSectionDisabled : iconSectionChecked;
  } else {
    iconSectionStyle = iconSection;
  }

  return (
    <div className={placementCard}>
      <img
        className={iconSectionStyle}
        src={props.icon}
        alt={props.label}
      />
      <span id="placements-1p-target-placement-label" className={titleStyle}>
        {props.label}
      </span><br />
      <span className={subheaderStyle}>
        {props.value}
      </span>
    </div>
  );
};

renderPlacements.propTypes = {
  props: PropTypes.shape({
    checked: PropTypes.bool.isRequired,
    disabled: PropTypes.bool.isRequired,
    icon: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }),
};

const ValidatedPlacementsFunctionPickerInternal = ({
  updateValidation,
  validationProps,
}) => {
  const i18n = useContext(I18nContext);
  const { data: { BoostAdunitIds } } = validationProps;
  const initialSelectedKey = 'all';
  const initialSelectedPlacements = useMemo(() => _.map(placements, (placement => placement.id)), []);
  const initialSelectedAdUnitIds = useMemo(() =>
    _.flatten(_.map(
      initialSelectedPlacements,
      i => placements[placementsIdMap[i]].adUnitIds
    )), [initialSelectedPlacements]);

  const [selectedKey, setSelectedKey] = useState(initialSelectedKey);
  const [selectedPlacements, setSelectedPlacements] = useState(initialSelectedPlacements);
  const [selectedAdUnitIds, setSelectedAdUnitIds] = useState(initialSelectedAdUnitIds);
  const [placementsDisabledStates, setPlacementsDisabledStates] = useState([false, false, false]);

  useEffect(() => {
    if (BoostAdunitIds && _.isArray(BoostAdunitIds) && BoostAdunitIds.length > 0) {
      const savedSelectedKey = 'specific';
      setSelectedKey(savedSelectedKey);

      let savedSelectedPlacements = [];
      let savedSelectedAdUnitIds = [];
      _.forEach(placements, (placement) => {
        const overlap = _.intersection(BoostAdunitIds, placement.adUnitIds);
        if (overlap.length > 0) {
          savedSelectedPlacements = savedSelectedPlacements.concat(placement.id);
          savedSelectedAdUnitIds = savedSelectedAdUnitIds.concat(placement.adUnitIds);
        }
      });

      const newPlacementsDisabledStates = [false, false, false];
      if (savedSelectedPlacements.length === 1) {
        const idToDisable = savedSelectedPlacements[0];
        newPlacementsDisabledStates[idToDisable - 1] = true;
      }

      setPlacementsDisabledStates(newPlacementsDisabledStates);
      setSelectedPlacements(savedSelectedPlacements);
      setSelectedAdUnitIds(savedSelectedAdUnitIds);
    }
  }, [BoostAdunitIds, updateValidation]);

  const onChangeAllSpecific = useCallback((e, option) => {
    setSelectedKey(option.key);

    const result = {
      BoostAdunitIds: option.key === 'all' ? 'delete_value' : selectedAdUnitIds,
    };

    updateValidation([], result);
  }, [selectedAdUnitIds, updateValidation]);

  const handlePlacementOnChange = useCallback((e, isChecked, currentId, currentAdUnitIds) => {
    let newSelectedPlacements = [];
    let newSelectedAdUnitIds = [];

    // get the new selected placements and adUnitIds
    if (isChecked) {
      newSelectedPlacements = selectedPlacements.concat(currentId);
      newSelectedAdUnitIds = selectedAdUnitIds.concat(currentAdUnitIds);
    } else {
      newSelectedPlacements = _.filter(selectedPlacements, (placement => !_.isEqual(placement, currentId)));
      newSelectedAdUnitIds = _.flatten(_.filter(selectedAdUnitIds, (id => !_.contains(currentAdUnitIds, id))));
    }

    const newPlacementsDisabledStates = [false, false, false];
    if (newSelectedPlacements.length === 1) {
      const idToDisable = newSelectedPlacements[0];
      newPlacementsDisabledStates[idToDisable - 1] = true;
    }
    setPlacementsDisabledStates(newPlacementsDisabledStates);
    setSelectedPlacements(newSelectedPlacements);
    setSelectedAdUnitIds(newSelectedAdUnitIds);

    const result = {
      BoostAdunitIds: '',
    };
    result.BoostAdunitIds = newSelectedAdUnitIds;
    updateValidation([], result);
  }, [selectedPlacements, selectedAdUnitIds, updateValidation]);

  return (
    <div id="placements-1p-target-view">
      <div className={placementsAllSpecificChoiceGroup} >
        <ChoiceGroup
          id="placements-1p-all-specific-options"
          label={i18n.getString(_TL_('Where do you want to show your ads?'))}
          options={options}
          onChange={onChangeAllSpecific}
          selectedKey={selectedKey}
        />
      </div>
      <CampaignOData
        call={({ currentAccount }) => currentAccount.PlacementExclusionLists['Default.GetOwnedOperatedExclusions'].get()}
      >
        {oolist => (
           selectedKey === 'specific' && oolist.value.length > 0 &&
           <MessageBar
             id="placements-1p-target-message-bar"
             messageBarType={MessageBarType.severeWarning}
             isMultiline
             styles={itemStyle}
             className="targets-message-bar"
           >
             {i18n.getString(_TL_('You may be excluding the following websites which will prevent your campaign from serving'))}: { oolist.value.map(item => item.Url).join(', ')}
           </MessageBar>
                   )
        }
      </CampaignOData>
      {selectedKey === 'specific' && (
        <div id="placements-1p-target-checkboxes" className={placementsCheckboxes}>
          <Label>{i18n.getString(_TL_('Which bundles do you want your ads to serve in?'))}</Label>
          <Stack verticalAlign="center" horizontal wrap tokens={{ childrenGap: 8 }}>
            {
              _.map(placements, (placement) => {
                let currentItemStyle;
                if (placement.checked) {
                  currentItemStyle = placement.disabled ? itemStyleDisabled : itemStyleChecked;
                } else {
                  currentItemStyle = itemStyle;
                }
                const checkedState = _.contains(selectedPlacements, placement.id);
                const disabledState = placementsDisabledStates[placement.id - 1];

                return (
                  <div className={currentItemStyle}>
                    <Checkbox
                      id={placement.id}
                      i18n={i18n}
                      label={placement.label}
                      value={placement.description}
                      icon={placement.icon}
                      boxSide="end"
                      onRenderLabel={renderPlacements}
                      checked={checkedState}
                      disabled={disabledState}
                      onChange={(e, isChecked) => handlePlacementOnChange(e, isChecked, placement.id, placement.adUnitIds)}
                    />
                  </div>
                );
              })
            }
          </Stack>
        </div>
      )}
    </div>
  );
};

ValidatedPlacementsFunctionPickerInternal.propTypes = {
  updateValidation: PropTypes.func.isRequired,
  validationProps: PropTypes.shape({
    data: PropTypes.shape({
      BoostAdunitIds: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
  }).isRequired,
};

export const ValidatedPlacementsFunctionPicker = withCustomValidation(ValidatedPlacementsFunctionPickerInternal);
